import React, { useEffect, useState } from "react";
import {
  Building2,
  CalendarDays,
  ChevronRight,
  Clock3,
  FileText,
  GraduationCap,
  Handshake,
  Home,
  Package,
  Shield,
  ShieldCheck,
  Store,
  Stethoscope,
  User,
  Trash2,
  X,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Socio } from "../types/Socio.tsx";
import socioService from "../services/socioService.tsx";
import { useAuth0 } from "@auth0/auth0-react";
import {
  asociarSocioAServicio,
  crearSolicitudServicio,
  eliminarAsociacion,
  obtenerServiciosCatalogo,
  obtenerServiciosDeSocio,
  listarSolicitudesServicio,
  ServicioCatalogo,
} from "../services/serviciosAsociacionService.ts";
import { useAdminRole } from "../hooks/useAdminRole.ts";
import { getServiceIcon } from "../utils/serviceIcons.ts";
import { getDeudaSocio } from "../services/cuotasService.tsx";

interface DashboardProps {
  socio?: Socio;
  isAdminView?: boolean;
}

type Service = {
  id?: number;
  title: string;
  description: string;
  link: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconKey?: string;
  accent: string;
  badge?: string;
  category: string;
};

type PendingService = {
  servicioId: number;
  servicio: string;
  categoria?: string;
  createdAt: string;
};

const ServiceCard = ({
  service,
  navigate,
  rightAction,
}: {
  service: Service;
  navigate: (path: string) => void;
  rightAction?: React.ReactNode;
}) => {
  const Icon = service.icon;
  return (
    <div className="relative flex h-full flex-col justify-between overflow-visible rounded-2xl border border-slate-100 bg-white/70 p-5 pt-8 shadow-lg transition hover:-translate-y-1 hover:shadow-xl">
      <div className={`absolute -left-3 -top-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${service.accent} text-white shadow-lg border-2 border-blue-900/60 drop-shadow-lg`}>
        <Icon className="h-6 w-6" strokeWidth={2.2} />
      </div>
      <div className="flex items-center gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {service.category}
          </p>
          <h3 className="text-lg font-semibold text-slate-900">
            {service.title}
          </h3>
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-600">{service.description}</p>
      <div className="mt-4 flex items-center justify-between">
        {service.badge && (
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
            {service.badge}
          </span>
        )}
        <div className="flex items-center gap-2">{rightAction}</div>
      </div>
    </div>
  );
};

export default function ServiciosPanel({ socio: propSocio }: DashboardProps) {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAdminRole();
  const [socioData, setSocioData] = useState<Socio | null>(propSocio ?? null);
  const socioNombre = socioData?.socio_nombre ?? "socio";
  const socioEmail = socioData?.socio_mail ?? "";
  const socioTelefono = socioData?.socio_celular ?? "";
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState<string>("");
  const [selectedServiceId, setSelectedServiceId] = useState<number | undefined>();
  const [selectedServiceCategoria, setSelectedServiceCategoria] = useState<string>("");
  const [selectedServiceDescripcion, setSelectedServiceDescripcion] = useState<string>("");
  const [selectedServiceIconKey, setSelectedServiceIconKey] = useState<string>("auto");
  const [modalAction, setModalAction] = useState<"solicitar" | "asignar">("solicitar");
  const [formData, setFormData] = useState({
    nombre: socioNombre,
    email: socioEmail,
    telefono: socioTelefono,
    detalle: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [serviciosCatalogo, setServiciosCatalogo] = useState<ServicioCatalogo[]>([]);
  const [msgSolicitud, setMsgSolicitud] = useState<string | null>(null);
  const [enviandoSolicitud, setEnviandoSolicitud] = useState(false);
  const [msgAsignacion, setMsgAsignacion] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [msgAsociacion, setMsgAsociacion] = useState<string | null>(null);
  const [asociando, setAsociando] = useState(false);
  const [loadingAsociados, setLoadingAsociados] = useState(false);
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);
  const [resumenCuenta, setResumenCuenta] = useState<{
    debe: number;
    haber: number;
    saldo: number;
    estado: "deuda" | "favor" | "consolidado";
  }>({ debe: 0, haber: 0, saldo: 0, estado: "consolidado" });

  const [asociados, setAsociados] = useState<Service[]>([]);
  const [pendientes, setPendientes] = useState<PendingService[]>([]);
  const [servicioDetalle, setServicioDetalle] = useState<Service | null>(null);

  const catalogServices: Service[] = [
    {
      title: "Acompañamiento en aperturas y habilitaciones",
      description: "Guía práctica en habilitaciones comerciales y requisitos municipales.",
      link: "#",
      icon: Building2,
      accent: "from-indigo-400 to-blue-500",
      category: "Gestión",
    },
    {
      title: "Microcréditos",
      description: "Líneas de crédito para capital de trabajo con condiciones especiales.",
      link: "#",
      icon: Handshake,
      accent: "from-amber-400 to-orange-500",
      category: "Finanzas",
    },
    {
      title: "Sistema PAQ.AR para tiendas online",
      description: "Integra tu ecommerce con pasarela de pagos y logística simplificada.",
      link: "#",
      icon: Package,
      accent: "from-cyan-500 to-blue-500",
      category: "Digital",
    },
    {
      title: "Participación en ferias y eventos",
      description: "Cupos preferenciales para rondas de negocios y ferias regionales.",
      link: "#",
      icon: CalendarDays,
      accent: "from-rose-400 to-orange-500",
      category: "Redes",
    },
    {
      title: "Sellado de ARBA",
      description: "Tramitamos tu sellado y certificaciones de manera ágil.",
      link: "#",
      icon: Shield,
      accent: "from-slate-500 to-gray-700",
      category: "Gestión",
    },
    {
      title: "Convenios logísticos",
      description: "Beneficios con Andreani y operadores locales para tus envíos.",
      link: "#",
      icon: Store,
      accent: "from-green-400 to-emerald-500",
      category: "Operaciones",
    },
  ];

  useEffect(() => {
    const loadSocio = async () => {
      if (propSocio) {
        setSocioData(propSocio);
        return;
      }
      const stored = localStorage.getItem("socioSeleccionado");
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as Socio;
          setSocioData(parsed);
          if (parsed.socio_id) {
            const full = await socioService.getSocioById(
              parsed.socio_id,
              getAccessTokenSilently
            );
            setSocioData(full);
          }
        } catch (error) {
          console.error("Error al leer socio seleccionado:", error);
        }
      }
    };
    loadSocio();
  }, [propSocio, getAccessTokenSilently]);

  useEffect(() => {
    const loadCuentaDeuda = async () => {
      if (!socioData?.socio_id) return;
      try {
        // Fuente oficial: /pagos-socios/deuda_socio/:id
        const deuda = await getDeudaSocio(Number(socioData.socio_id), getAccessTokenSilently);
        const deudaSocio = Number(deuda?.deuda_socio ?? 0);
        const saldo = Number((deudaSocio || 0).toFixed(2));
        const estado = saldo > 0 ? "deuda" : saldo < 0 ? "favor" : "consolidado";

        setResumenCuenta({
          debe: Math.max(saldo, 0),
          haber: saldo < 0 ? Math.abs(saldo) : 0,
          saldo,
          estado,
        });
      } catch {
        setResumenCuenta({ debe: 0, haber: 0, saldo: 0, estado: "consolidado" });
      }
    };

    loadCuentaDeuda();
  }, [socioData?.socio_id, getAccessTokenSilently]);

  useEffect(() => {
    const loadAsociados = async () => {
      if (!socioData?.socio_id) return;
      try {
        setLoadingAsociados(true);
        const token = await getAccessTokenSilently();
        const data = await obtenerServiciosDeSocio(Number(socioData.socio_id), token);
        const mapped: Service[] = (Array.isArray(data) ? data : []).map((s: any) => {
          const svc = s.servicio || {};
          const category = svc.categoria ?? s.categoria ?? "Servicio";
          const iconKey = svc.icono ?? s.icono ?? s.icon;
          return {
            id: svc.servicio_id ?? s.servicio_id ?? s.id ?? s.servicioId,
            title: svc.nombre ?? s.nombre ?? s.title ?? "Servicio",
            description: svc.descripcion ?? s.descripcion ?? s.description ?? "",
            link: "#",
            icon: getServiceIcon(iconKey, category),
            iconKey: typeof iconKey === "string" ? iconKey : undefined,
            accent: category.toLowerCase().includes("salud")
              ? "from-emerald-400 to-teal-500"
              : category.toLowerCase().includes("seguridad")
              ? "from-blue-500 to-indigo-600"
              : "from-blue-500 to-indigo-600",
            badge: category,
            category,
          };
        });
        setAsociados(mapped);
      } catch (err) {
        console.error("No se pudieron cargar los servicios asociados del socio", err);
      } finally {
        setLoadingAsociados(false);
      }
    };
    loadAsociados();
  }, [socioData?.socio_id, getAccessTokenSilently]);

  useEffect(() => {
    const cargarServicios = async () => {
      try {
        setLoadingCatalogo(true);
        const token = await getAccessTokenSilently();
        const data = await obtenerServiciosCatalogo(token);
        setServiciosCatalogo(data || []);
      } catch (err) {
        console.error("No se pudo cargar el catálogo de servicios", err);
      } finally {
        setLoadingCatalogo(false);
      }
    };
    cargarServicios();
  }, [getAccessTokenSilently]);

  const pendingKey = socioData?.socio_id ? `serviciosPendientes:${socioData.socio_id}` : null;

  useEffect(() => {
    const loadPendientes = async () => {
      if (!socioData?.socio_id) {
        setPendientes([]);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const apiPendientes = await listarSolicitudesServicio({ estado: 'pendiente', socioId: Number(socioData.socio_id) }, token);
        const mapped = (apiPendientes || []).map((p: any) => ({
          servicioId: Number(p.servicio_id),
          servicio: p.servicio,
          categoria: undefined,
          createdAt: p.creado_en,
        }));
        setPendientes(mapped);
      } catch {
        if (!pendingKey) return;
        try {
          const raw = localStorage.getItem(pendingKey);
          const parsed = raw ? JSON.parse(raw) : [];
          setPendientes(Array.isArray(parsed) ? parsed : []);
        } catch {
          setPendientes([]);
        }
      }
    };

    loadPendientes();
  }, [pendingKey, socioData?.socio_id, getAccessTokenSilently]);

  const savePendientes = (next: PendingService[]) => {
    setPendientes(next);
    if (!pendingKey) return;
    localStorage.setItem(pendingKey, JSON.stringify(next));
  };

  // Si el catálogo trae el id de un servicio ya mostrado como asociado, sincronizamos el id
  useEffect(() => {
    if (!serviciosCatalogo.length) return;
    setAsociados((prev) =>
      prev.map((s) => {
        if (s.id) return s;
        const match = serviciosCatalogo.find(
          (c: any) =>
            c.id === s.id ||
            c.servicio_id === s.id ||
            c.nombre === s.title ||
            c.title === s.title
        );
        if (match) {
          return { ...s, id: match.id ?? match.servicio_id };
        }
        return s;
      })
    );
  }, [serviciosCatalogo]);


  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      nombre: socioNombre,
      email: socioEmail,
      telefono: socioTelefono,
    }));
  }, [socioNombre, socioEmail, socioTelefono]);

  useEffect(() => {
    if (!pendientes.length || !asociados.length) return;
    const asociadosIds = new Set(asociados.map((a) => String(a.id)).filter(Boolean));
    const remaining = pendientes.filter((p) => !asociadosIds.has(String(p.servicioId)));
    if (remaining.length !== pendientes.length) {
      savePendientes(remaining);
    }
  }, [asociados]);

  const openForm = (
    service: { id?: number; title: string; categoria?: string; descripcion?: string; iconKey?: string },
    action: "solicitar" | "asignar" = "solicitar"
  ) => {
    setSelectedService(service.title);
    setSelectedServiceId(service.id);
    setSelectedServiceCategoria(service.categoria || "");
    setSelectedServiceDescripcion(service.descripcion || "");
    setSelectedServiceIconKey(service.iconKey || "auto");
    setModalAction(action);
    setShowForm(true);
    setSubmitted(false);
    setMsgSolicitud(null);
    setMsgAsignacion(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    enviarSolicitudServicio();
  };

  const enviarSolicitudServicio = async () => {
    if (!selectedServiceId) {
      setMsgSolicitud("Elegí un servicio válido para asociar.");
      return;
    }
    if (!selectedService) {
      setMsgSolicitud("No pudimos identificar el servicio seleccionado.");
      return;
    }
    if (!socioData?.socio_id) {
      setMsgSolicitud("No hay socio seleccionado. Elegí un socio primero.");
      return;
    }
    const contactoNombre = (formData.nombre || socioData.socio_nombre || "").trim();
    const contactoEmail = (formData.email || socioData.socio_mail || "").trim();
    const contactoTelefono = (formData.telefono || socioData.socio_celular || socioData.socio_telefono || "").trim();
    const comentarios = formData.detalle?.trim();

    if (!contactoNombre) {
      setMsgSolicitud("Ingresá un nombre de contacto.");
      return;
    }
    if (!contactoEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contactoEmail)) {
      setMsgSolicitud("Ingresá un email válido.");
      return;
    }
    try {
      setMsgSolicitud(null);
      setEnviandoSolicitud(true);
      setSubmitted(false);
      const token = await getAccessTokenSilently();
      const asociacionPayload = {
        socioId: Number(socioData.socio_id),
        servicioId: Number(selectedServiceId),
        notas: comentarios || undefined,
        contacto: {
          email: contactoEmail || undefined,
          telefono: contactoTelefono || undefined,
          nombre: contactoNombre || undefined,
        },
      };
      const solicitudPayload = {
        socioId: Number(socioData.socio_id),
        servicioId: Number(selectedServiceId),
        servicio: selectedService,
        nombre: contactoNombre,
        email: contactoEmail,
        telefono: contactoTelefono || undefined,
        comentarios: comentarios || undefined,
      };
      if (modalAction === "asignar") {
        await asociarSocioAServicio(asociacionPayload, token);
        setSubmitted(true);
        setMsgSolicitud(
          `Servicio "${selectedService}" asignado a ${formData.nombre || socioData?.socio_nombre || "el socio"}.`
        );
      } else {
        await crearSolicitudServicio(solicitudPayload, token);
        const alreadyPending = pendientes.some((p) => String(p.servicioId) === String(selectedServiceId));
        if (!alreadyPending) {
          savePendientes([
            ...pendientes,
            {
              servicioId: Number(selectedServiceId),
              servicio: selectedService,
              categoria: selectedServiceCategoria || undefined,
              createdAt: new Date().toISOString(),
            },
          ]);
        }
        setSubmitted(true);
        setMsgSolicitud("Solicitud enviada. Queda pendiente de aprobación por administración.");
      }
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message || err?.message || "No se pudo enviar la solicitud.";
      setMsgSolicitud(apiMsg);
    } finally {
      setEnviandoSolicitud(false);
    }
  };

  const asignarServicioDirecto = async (service: Service) => {
    if (!isAdmin) {
      setMsgAsignacion("Solo administradores pueden asignar servicios.");
      return;
    }
    if (!socioData?.socio_id || !service.id) {
      setMsgAsignacion("Elegí un servicio y asegurate de tener un socio cargado.");
      return;
    }
    setAssigningId(service.id);
    setMsgAsignacion(null);
    try {
      const token = await getAccessTokenSilently();
      await asociarSocioAServicio(
        {
          socioId: Number(socioData.socio_id),
          servicioId: Number(service.id),
          notas: formData.detalle || undefined,
          contacto: {
            email: formData.email || undefined,
            telefono: formData.telefono || undefined,
            nombre: formData.nombre || undefined,
          },
        },
        token
      );
      setAsociados((prev) => {
        const filtered = prev.filter(
          (s) =>
            !(
              (s.id && service.id && String(s.id) === String(service.id)) ||
              (!s.id && s.title === service.title)
            )
        );
        return [...filtered, service];
      });
      setMsgAsignacion(
        `Servicio "${service.title}" asignado a ${socioData.socio_nombre || socioData.socio_id}.`
      );
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message || err?.message || "No se pudo asignar el servicio.";
      setMsgAsignacion(apiMsg);
    } finally {
      setAssigningId(null);
    }
  };

  const asociarServicio = async (servicioId?: number) => {
    if (!socioData?.socio_id || !servicioId) {
      setMsgAsociacion("Elegí un servicio y asegurate de tener un socio cargado.");
      return;
    }
    if (formData.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email)) {
      setMsgAsociacion("Ingresá un email válido.");
      return;
    }
    try {
      setAsociando(true);
      setMsgAsociacion(null);
      const token = await getAccessTokenSilently();
      await asociarSocioAServicio(
        {
          socioId: Number(socioData.socio_id),
          servicioId: Number(servicioId),
          notas: formData.detalle || undefined,
          contacto: {
            email: formData.email || undefined,
            telefono: formData.telefono || undefined,
            nombre: formData.nombre || undefined,
          },
        },
        token
      );
      setMsgAsociacion("Servicio asociado correctamente.");
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message || err?.message || "No se pudo asociar el servicio.";
      setMsgAsociacion(apiMsg);
    } finally {
      setAsociando(false);
    }
  };

  const desasociarServicio = async (service: Service) => {
    if (!isAdmin) {
      setMsgAsignacion("Solo administradores pueden desasignar servicios.");
      return;
    }
    if (!socioData?.socio_id || !service.id) {
      setMsgAsignacion("No se pudo desasignar: falta socio o servicio.");
      return;
    }
    setRemovingId(service.id);
    setMsgAsignacion(null);
    try {
      const token = await getAccessTokenSilently();
      await eliminarAsociacion(Number(socioData.socio_id), Number(service.id), token);
      setAsociados((prev) =>
        prev.filter(
          (s) =>
            !(
              (s.id && service.id && String(s.id) === String(service.id)) ||
              (!s.id && s.title === service.title)
            )
        )
      );
      setMsgAsignacion(`Servicio "${service.title}" desasignado.`);
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message || err?.message || "No se pudo desasignar el servicio.";
      setMsgAsignacion(apiMsg);
    } finally {
      setRemovingId(null);
    }
  };

  const isServicioPendiente = (serviceId?: number, serviceTitle?: string) =>
    pendientes.some(
      (p) =>
        (serviceId && String(p.servicioId) === String(serviceId)) ||
        (!!serviceTitle && p.servicio.toLowerCase() === serviceTitle.toLowerCase())
    );

  return (
    <div className="mx-auto max-w-7xl px-4 pb-10 pt-6 lg:px-10">
      <nav className="mb-6 flex" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              to="/inicio"
              className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              <Home className="mr-2 h-4 w-4" />
              Inicio
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">
                Servicios
              </span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="fixed right-4 top-24 z-40">
        <button
          type="button"
          onClick={() => navigate('/pagos?tipo=servicio')}
          className={`inline-flex rounded-full px-3 py-1.5 text-sm font-semibold shadow-lg ${
            resumenCuenta.estado === 'deuda'
              ? 'bg-amber-100 text-amber-900 border border-amber-300'
              : resumenCuenta.estado === 'favor'
                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                : 'bg-slate-100 text-slate-700 border border-slate-300'
          }`}
          title="Ver pagos filtrados por servicios"
        >
          {resumenCuenta.estado === 'deuda' && `Deuda servicios: $${Math.abs(resumenCuenta.saldo).toLocaleString('es-AR')}`}
          {resumenCuenta.estado === 'favor' && `Saldo a favor: $${Math.abs(resumenCuenta.saldo).toLocaleString('es-AR')}`}
          {resumenCuenta.estado === 'consolidado' && 'Sin deuda de servicios'}
        </button>
      </div>

      {pendientes.length > 0 && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-amber-800">Solicitudes pendientes de aprobación</p>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-amber-700">
              {pendientes.length} pendiente(s)
            </span>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {pendientes.map((p) => (
              <div key={`${p.servicioId}-${p.createdAt}`} className="rounded-xl border border-amber-200 bg-white p-3">
                <p className="text-sm font-semibold text-slate-900">{p.servicio}</p>
                <p className="text-xs text-slate-600">Servicio ID: {p.servicioId}</p>
                <p className="text-xs text-slate-500">Solicitado: {String(p.createdAt || '').slice(0, 10)}</p>
                <span className="mt-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">
                  Pendiente de aprobación
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <section className="mt-10 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-blue-600">
              Servicios vinculados a un socio
            </p>
            <h2 className="text-2xl font-bold text-slate-900">
              Accesos y beneficios activos
            </h2>
            <p className="text-sm text-slate-600">
              Información rápida de los servicios que ya podés usar con tu cuenta.
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">
            <User className="h-4 w-4" />
            Asociado a {socioNombre}
          </div>
        </div>
        {msgAsignacion && (
          <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800">
            {msgAsignacion}
          </div>
        )}
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4 items-stretch">
          {loadingAsociados
            ? Array.from({ length: 4 }).map((_, idx) => (
                <div
                  key={`sk-asoc-${idx}`}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-slate-200 animate-pulse" />
                    <div className="space-y-2">
                      <div className="h-3 w-24 rounded bg-slate-200 animate-pulse" />
                      <div className="h-4 w-32 rounded bg-slate-200 animate-pulse" />
                    </div>
                  </div>
                  <div className="mt-3 h-10 w-full rounded bg-slate-100 animate-pulse" />
                </div>
              ))
            : asociados.map((service) => (
                <div key={service.id ?? service.title} className="relative">
                  <ServiceCard
                    service={service}
                    navigate={navigate}
                    rightAction={
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                          Activo
                        </span>
                        {service.id && isAdmin && (
                          <button
                            type="button"
                            onClick={() => desasociarServicio(service)}
                            disabled={removingId === service.id}
                            className="inline-flex items-center gap-1 rounded-full border border-rose-100 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                            title="Desasignar servicio"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            {removingId === service.id ? "Quitando..." : "Quitar"}
                          </button>
                        )}
                      </div>
                    }
                  />
                </div>
              ))}
        </div>
      </section>

      <section className="mt-12 space-y-5">
        <div>
          <p className="text-sm font-semibold text-blue-600">Catálogo</p>
          <h2 className="text-2xl font-bold text-slate-900">
            Más servicios para impulsar tu negocio
          </h2>
          <p className="text-sm text-slate-600">
            Sumá acompañamiento en gestión, eventos y convenios logísticos.
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 items-stretch">
          {loadingCatalogo
            ? Array.from({ length: 3 }).map((_, idx) => (
                <div
                  key={`sk-cat-${idx}`}
                  className="flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-slate-200 animate-pulse" />
                    <div className="space-y-2">
                      <div className="h-3 w-24 rounded bg-slate-200 animate-pulse" />
                      <div className="h-4 w-32 rounded bg-slate-200 animate-pulse" />
                    </div>
                  </div>
                  <div className="mt-3 h-10 w-full rounded bg-slate-100 animate-pulse" />
                </div>
              ))
            : (serviciosCatalogo.length ? serviciosCatalogo : catalogServices)
                .filter((s: any) => s.activo !== false)
                .map((service: any) => {
                const rawIcon = service.icono ?? service.iconKey ?? service.icon;
                const cardData: Service = {
                  id: service.id ?? service.servicio_id,
                  title: service.title ?? service.nombre,
                  description: service.description ?? service.descripcion,
                  link: "#",
                  icon: getServiceIcon(rawIcon, service.categoria),
                  iconKey: typeof rawIcon === "string" ? rawIcon : undefined,
                  accent: service.accent || "from-blue-500 to-indigo-500",
                  badge: service.categoria,
                  category: service.categoria || "Servicio",
                };
                const alreadyAssigned = asociados.some(
                  (s) => s.id && cardData.id && String(s.id) === String(cardData.id)
                );
                const isPending = isServicioPendiente(cardData.id, cardData.title);
                return (
                  <div key={service.id ?? service.title} className="relative flex h-full flex-col">
                    <ServiceCard
                      service={cardData}
                      navigate={navigate}
                      rightAction={
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setServicioDetalle(cardData)}
                            className="rounded-full bg-sky-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-sky-600"
                          >
                            Detalle
                          </button>
                          {cardData.id && (
                            <>
                              <button
                                type="button"
                                disabled={alreadyAssigned || isPending}
                                onClick={() =>
                                  openForm(
                                    {
                                      id: cardData.id,
                                      title: cardData.title,
                                      categoria: cardData.category,
                                      descripcion: cardData.description,
                                      iconKey: cardData.iconKey,
                                    },
                                    "solicitar"
                                  )
                                }
                                className={`rounded-full px-3 py-1 text-xs font-semibold shadow-sm transition ${
                                  alreadyAssigned
                                    ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                                    : isPending
                                      ? "bg-amber-100 text-amber-800 cursor-not-allowed"
                                      : "bg-blue-600 text-white hover:bg-blue-700"
                                }`}
                              >
                                {alreadyAssigned ? "Ya asignado" : isPending ? "Pendiente de aprobación" : "Solicitar"}
                              </button>
                              {isAdmin && (
                                <button
                                  type="button"
                                  disabled={assigningId === cardData.id || alreadyAssigned}
                                  onClick={() => asignarServicioDirecto(cardData)}
                                  className={`rounded-full px-3 py-1 text-xs font-semibold shadow-sm transition ${
                                    alreadyAssigned
                                      ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                                      : "bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                                  }`}
                                >
                                  {assigningId === cardData.id ? "Asignando..." : "Asignar"}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      }
                    />
                  </div>
                );
              })}
        </div>
      </section>

      {servicioDetalle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur transition-opacity duration-200 ease-out">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl transition-all duration-200 ease-out animate-[fadeIn_.2s_ease-out]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Detalle</p>
                <h3 className="text-xl font-bold text-slate-900">
                  {servicioDetalle.title}
                </h3>
                <p className="text-sm text-slate-600">
                  {servicioDetalle.category || "Servicio"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setServicioDetalle(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                {servicioDetalle.description || "Sin descripción"}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
                  Estado: Activo
                </span>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                  Categoría: {servicioDetalle.category || "Servicio"}
                </span>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setServicioDetalle(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
              <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase text-blue-600">
                      {modalAction === "asignar" ? "Asignar servicio" : "Solicitud de servicio"}
                    </p>
                    <h3 className="text-xl font-bold text-slate-900">
                      {selectedService}
                    </h3>
                    {(selectedServiceCategoria || selectedServiceDescripcion) && (
                      <p className="text-sm text-slate-600">
                        {selectedServiceCategoria && <span className="font-semibold">{selectedServiceCategoria}</span>}{" "}
                        {selectedServiceDescripcion}
                      </p>
                    )}
                  </div>
                  <button
                    className="text-slate-500 hover:text-slate-800"
                    onClick={() => setShowForm(false)}
                  >
                ✕
              </button>
            </div>
            <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-semibold text-slate-700">
                  Nombre y apellido
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, nombre: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </label>
                <label className="text-sm font-semibold text-slate-700">
                  Email
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, email: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </label>
              </div>
              <label className="text-sm font-semibold text-slate-700">
                Teléfono
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, telefono: e.target.value }))
                  }
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Comentarios o necesidad
                <textarea
                  value={formData.detalle}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, detalle: e.target.value }))
                  }
                  className="mt-1 min-h-[100px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Contanos qué necesitás o cómo querés usar este servicio."
                />
              </label>
              {submitted && (
                <p className="rounded-lg bg-green-50 px-3 py-2 text-sm font-semibold text-green-700">
                  ¡Solicitud enviada! Nos contactaremos a la brevedad.
                </p>
              )}
              {msgSolicitud && (
                <p className="text-sm text-slate-700">{msgSolicitud}</p>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={enviandoSolicitud}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
                >
                  {enviandoSolicitud ? "Enviando..." : "Enviar solicitud"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
